/**
 * Site-wide advertising config (header / mid-roll / footer carousels).
 * Stored as JSON in site_advertising_config.config; validated here.
 */

export const SITE_ADVERTISING_CONFIG_ID = 'a0000000-0000-4000-8000-000000000001';

export type AdAudience = 'all' | 'guests_only' | 'signed_in_only';

export type AdShell = 'public' | 'member';

export type AdPathRuleMode = 'off' | 'allowlist' | 'denylist';

export interface AdPathRules {
  mode: AdPathRuleMode;
  patterns: string[];
}

export interface AdSlide {
  imageUrl: string;
  href?: string;
  alt?: string;
}

export interface AdSlotConfig {
  enabled: boolean;
  slides: AdSlide[];
  audience: AdAudience;
  shells: AdShell[];
  pathRules: AdPathRules;
  maxHeightPx: number;
  autoplaySeconds: number;
  showDots: boolean;
  showArrows: boolean;
}

export interface SiteAdvertisingDocument {
  version: number;
  header: AdSlotConfig;
  midRoll: AdSlotConfig;
  footer: AdSlotConfig;
}

export const MAX_AD_SLIDES = 4;

/** Display and crop aspect for ad banners: width ÷ height (3 = 3:1). Matches ImageCropModal / export. */
export const AD_BANNER_ASPECT_WIDTH_OVER_HEIGHT = 3;

/**
 * Strips to undefined if empty. Keeps `http(s):`, `mailto:`, and same-site paths (`/...`, `#...`).
 * Otherwise prepends `https://` so `www.example.com` is not treated as a relative path.
 */
export function normalizeAdSlideHref(raw: string): string | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  if (t.startsWith('//')) return `https:${t}`;
  if (t.startsWith('/')) return t;
  if (t.startsWith('#')) return t;
  return `https://${t}`;
}

export function defaultAdSlotConfig(): AdSlotConfig {
  return {
    enabled: false,
    slides: [],
    audience: 'all',
    shells: ['public', 'member'],
    pathRules: { mode: 'off', patterns: [] },
    maxHeightPx: 200,
    autoplaySeconds: 0,
    showDots: true,
    showArrows: true,
  };
}

export function defaultSiteAdvertisingDocument(): SiteAdvertisingDocument {
  return {
    version: 1,
    header: defaultAdSlotConfig(),
    midRoll: defaultAdSlotConfig(),
    footer: defaultAdSlotConfig(),
  };
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

function coerceAudience(v: unknown): AdAudience {
  if (v === 'guests_only' || v === 'signed_in_only' || v === 'all') return v;
  return 'all';
}

function coerceShells(v: unknown): AdShell[] {
  if (!Array.isArray(v)) return ['public', 'member'];
  const out: AdShell[] = [];
  for (const x of v) {
    if (x === 'public' || x === 'member') out.push(x);
  }
  return out.length > 0 ? out : ['public', 'member'];
}

function coercePathRules(v: unknown): AdPathRules {
  if (!v || typeof v !== 'object') return { mode: 'off', patterns: [] };
  const o = v as Record<string, unknown>;
  const mode = o.mode === 'allowlist' || o.mode === 'denylist' || o.mode === 'off' ? o.mode : 'off';
  const patterns = Array.isArray(o.patterns)
    ? o.patterns.filter((p): p is string => typeof p === 'string' && p.trim().length > 0).map((p) => p.trim())
    : [];
  return { mode, patterns };
}

function coerceSlides(v: unknown): AdSlide[] {
  if (!Array.isArray(v)) return [];
  const out: AdSlide[] = [];
  for (const item of v) {
    if (out.length >= MAX_AD_SLIDES) break;
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (!isNonEmptyString(o.imageUrl)) continue;
    const slide: AdSlide = {
      imageUrl: String(o.imageUrl).trim(),
    };
    if (isNonEmptyString(o.href)) {
      const n = normalizeAdSlideHref(String(o.href));
      if (n) slide.href = n;
    }
    if (isNonEmptyString(o.alt)) slide.alt = String(o.alt).trim();
    out.push(slide);
  }
  return out;
}

function clampInt(n: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Merge partial JSON from DB with defaults; safe for untrusted JSON. */
export function parseSiteAdvertisingConfig(raw: unknown): SiteAdvertisingDocument {
  const base = defaultSiteAdvertisingDocument();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const version = typeof o.version === 'number' && o.version >= 1 ? Math.floor(o.version) : 1;

  const mergeSlot = (key: 'header' | 'midRoll' | 'footer'): AdSlotConfig => {
    const def = defaultAdSlotConfig();
    const s = o[key];
    if (!s || typeof s !== 'object') return def;
    const so = s as Record<string, unknown>;
    return {
      enabled: so.enabled === true,
      slides: coerceSlides(so.slides),
      audience: coerceAudience(so.audience),
      shells: coerceShells(so.shells),
      pathRules: coercePathRules(so.pathRules),
      maxHeightPx: clampInt(Number(so.maxHeightPx), 80, 600, def.maxHeightPx),
      autoplaySeconds: clampInt(Number(so.autoplaySeconds), 0, 60, 0),
      showDots: so.showDots !== false,
      showArrows: so.showArrows !== false,
    };
  };

  return {
    version,
    header: mergeSlot('header'),
    midRoll: mergeSlot('midRoll'),
    footer: mergeSlot('footer'),
  };
}

/**
 * Path pattern rules:
 * - Normalized path must start with pattern (prefix match).
 * - If pattern ends with `*`, the prefix before `*` must match the start of the path
 *   (same as common glob "starts with").
 * Examples: `/pricing`, `/tournaments/*`, `/`
 */
export function matchesPathPattern(pathname: string, patternRaw: string): boolean {
  const pattern = patternRaw.trim();
  if (!pattern) return false;
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;

  if (pattern === '/') {
    return path === '/' || path === '';
  }

  if (pattern.endsWith('*')) {
    const prefix = pattern.slice(0, -1);
    if (prefix === '') return true;
    return path.startsWith(prefix);
  }

  return path === pattern || path.startsWith(`${pattern}/`);
}

export function pathRulesAllow(pathname: string, rules: AdPathRules): boolean {
  if (rules.mode === 'off') return true;
  const pats = rules.patterns;
  const anyMatch = pats.some((pat) => matchesPathPattern(pathname, pat));
  if (rules.mode === 'allowlist') {
    if (pats.length === 0) return false;
    return anyMatch;
  }
  if (pats.length === 0) return true;
  return !anyMatch;
}

export function audienceAllows(audience: AdAudience, isAuthenticated: boolean): boolean {
  if (audience === 'all') return true;
  if (audience === 'guests_only') return !isAuthenticated;
  return isAuthenticated;
}

/**
 * Whether this layout shell may show the slot for the given audience.
 * - `all`: respect shell checkboxes (public marketing vs member app).
 * - `signed_in_only`: signed-in users browse both areas; shell checkboxes are ignored so
 *   “Member app only” does not hide ads on /, /tournaments, etc. Use path rules to narrow.
 * - `guests_only`: only the public marketing layout; still requires “Public marketing site”.
 */
export function shellAllows(shells: AdShell[], shell: AdShell, audience: AdAudience): boolean {
  if (audience === 'signed_in_only') {
    return true;
  }
  if (audience === 'guests_only') {
    return shell === 'public' && shells.includes('public');
  }
  return shells.includes(shell);
}

export type AdSlotKey = 'header' | 'midRoll' | 'footer';

export function slotShouldDisplay(
  slot: AdSlotConfig,
  ctx: { pathname: string; isAuthenticated: boolean; shell: AdShell }
): boolean {
  if (!slot.enabled || slot.slides.length === 0) return false;
  if (!audienceAllows(slot.audience, ctx.isAuthenticated)) return false;
  if (!shellAllows(slot.shells, ctx.shell, slot.audience)) return false;
  if (!pathRulesAllow(ctx.pathname, slot.pathRules)) return false;
  return true;
}

/** Serialize for DB (drops empty optional fields normalization). */
export function serializeSiteAdvertisingDocument(doc: SiteAdvertisingDocument): SiteAdvertisingDocument {
  const serSlot = (s: AdSlotConfig): AdSlotConfig => ({
    ...s,
    slides: s.slides.slice(0, MAX_AD_SLIDES).map((sl) => {
      const href = sl.href?.trim() ? normalizeAdSlideHref(sl.href) : undefined;
      return {
        imageUrl: sl.imageUrl.trim(),
        ...(href ? { href } : {}),
        ...(sl.alt?.trim() ? { alt: sl.alt.trim() } : {}),
      };
    }),
    pathRules: {
      mode: s.pathRules.mode,
      patterns: s.pathRules.patterns.map((p) => p.trim()).filter(Boolean),
    },
  });
  return {
    version: doc.version,
    header: serSlot(doc.header),
    midRoll: serSlot(doc.midRoll),
    footer: serSlot(doc.footer),
  };
}
